import { Box, Button, Grid, Group, LoadingOverlay, Stack, Title } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { Declarant } from '@e-origin/shared';

import { LeavePageNotification, MultiLangCodes, TextInput } from '@components';
import { useDeclarant, useUpdateDeclarant } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';

const representativeSchema = z.object({
  representative: z.object({
    name: z.string(),
    identificationNumber: z.string(),
    status: z.union([z.string(), z.number()]),
    address: z.object({
      street: z.string(),
      postcode: z.string(),
      city: z.string(),
      country: z.string(),
    }),
    contactPerson: z.object({
      name: z.string(),
      phoneNumber: z.string(),
      email: z.string().email({ message: 'Invalid email' }).optional(),
    }),
  }),
});

type RepresentativeFormValues = z.infer<typeof representativeSchema>;

interface RepresentativeFormProps {
  editDisabled: boolean;
}

export const RepresentativeForm: React.FC<RepresentativeFormProps> = ({ editDisabled }) => {
  const { data: declarant, isLoading } = useDeclarant();
  const updateDeclarantMutation = useUpdateDeclarant();
  const codelistSystems = useCodeListSystem({});

  const buildInitialValues = (d) => ({
    representative: {
      name: d?.representative?.name || '',
      identificationNumber: d?.representative?.identificationNumber || '',
      status: d?.representative?.status || '',
      address: {
        street: d?.representative?.address?.street || '',
        postcode: d?.representative?.address?.postcode || '',
        city: d?.representative?.address?.city || '',
        country: d?.representative?.address?.country || '',
      },
      contactPerson: {
        name: d?.representative?.contactPerson?.name || '',
        phoneNumber: d?.representative?.contactPerson?.phoneNumber || '',
        email: d?.representative?.contactPerson?.email || '',
      },
    },
  });

  const form = useFormWithNameInputProp<RepresentativeFormValues>({
    initialValues: buildInitialValues(declarant),
    validate: zodResolver(representativeSchema),
  });

  useEffect(() => {
    form.setValues(buildInitialValues(declarant));
    form.resetDirty({});
  }, [declarant]);

  const handleSubmit = (values: RepresentativeFormValues) => {
    if (declarant) {
      updateDeclarantMutation.mutate({
        declarantId: declarant._id,
        declarant: values as Declarant,
      });
    }
  };

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Box>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="xl">
          <Box>
            <Grid>
              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('representative.name')} label="Name" data-pw="representative-name-input" />
              </Grid.Col>
              <Grid.Col span={5}>
                <TextInput {...form.getInputProps('representative.identificationNumber')} label="Identification number" data-pw="representative-id-input" />
              </Grid.Col>
              <Grid.Col span={4}>
                <MultiLangCodes
                  {...form.getInputProps('representative.status')}
                  label="Status"
                  code="CL094"
                  system={codelistSystems}
                  value={form.values.representative.status.toString()}
                  onChange={(value) => form.setFieldValue('representative.status', Number(value))}
                  data-pw="representative-status-input"
                />
              </Grid.Col>
            </Grid>

            <Grid mt="md">
              <Grid.Col span={5}>
                <TextInput {...form.getInputProps('representative.address.street')} label="Address" data-pw="representative-address-input" />
              </Grid.Col>
              <Grid.Col span={2}>
                <TextInput {...form.getInputProps('representative.address.postcode')} label="Postcode" data-pw="representative-postcode-input" />
              </Grid.Col>
              <Grid.Col span={2}>
                <TextInput {...form.getInputProps('representative.address.city')} label="City" data-pw="representative-city-input" />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('representative.address.country')} label="Country" data-pw="representative-country-input" />
              </Grid.Col>
            </Grid>
          </Box>

          <Box>
            <Title order={4} fw={600} mb="md">
              Contact person
            </Title>
            <Grid>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('representative.contactPerson.name')} label="Name" data-pw="contact-name-input" />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('representative.contactPerson.phoneNumber')} label="Phone number" data-pw="contact-phone-input" />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('representative.contactPerson.email')} label="E-mail" data-pw="contact-email-input" />
              </Grid.Col>
            </Grid>
          </Box>

          <Group py="xl">
            <Button
              disabled={editDisabled || !form.isValid()}
              type="submit"
              variant="filled"
              size="lg"
              loading={updateDeclarantMutation.isPending}
              data-pw="save-representative-button"
            >
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Box>
  );
};
